import { Routes } from '@angular/router';
import { provideTranslocoScope } from '@jsverse/transloco';
import { authGuard, adminOnlyGuard, adminGuestGuard } from './admin/auth/auth.guard';
import { clientAuthGuard, clientGuestGuard, clientAdminGuard } from './client/auth/client-auth.guard';
import { resolveRootRedirect } from './core/auth-redirect';

export const appRoutes: Routes = [
  {
    path: 'admin',
    // Each portal loads only its own catalogue: public/i18n/admin/{lang}.json
    providers: [provideTranslocoScope('admin')],
    children: [
      {
        path: 'login',
        canActivate: [adminGuestGuard],
        loadComponent: () => import('./admin/login/login.component').then(m => m.LoginComponent)
      },
      {
        path: '',
        loadComponent: () => import('./admin/shell/admin-shell.component').then(m => m.AdminShellComponent),
        canActivate: [authGuard],
        children: [
          {
            path: 'dashboard',
            loadComponent: () => import('./admin/dashboard/dashboard.component').then(m => m.DashboardComponent)
          },
          {
            path: 'companies',
            canActivate: [adminOnlyGuard],
            loadComponent: () => import('./admin/companies/companies.component').then(m => m.CompaniesComponent)
          },
          {
            path: 'companies/:id',
            canActivate: [adminOnlyGuard],
            loadComponent: () => import('./admin/companies/company-detail.component').then(m => m.CompanyDetailComponent)
          },
          {
            path: 'projects',
            loadComponent: () => import('./admin/projects/projects.component').then(m => m.ProjectsComponent)
          },
          {
            path: 'projects/:id',
            loadComponent: () => import('./admin/projects/project-detail.component').then(m => m.ProjectDetailComponent)
          },
          {
            path: 'specialists',
            canActivate: [adminOnlyGuard],
            loadComponent: () => import('./admin/specialists/specialists.component').then(m => m.SpecialistsComponent)
          },
          {
            path: 'specialists/:id',
            canActivate: [adminOnlyGuard],
            loadComponent: () => import('./admin/specialists/specialist-detail.component').then(m => m.SpecialistDetailComponent)
          },
          {
            path: 'templates',
            loadComponent: () => import('./admin/vulnerability-templates/vulnerability-templates.component').then(m => m.VulnerabilityTemplatesComponent)
          },
          {
            path: 'profile',
            loadComponent: () => import('./admin/profile/profile.component').then(m => m.ProfileComponent)
          },
          {
            path: 'operations/:id',
            loadComponent: () => import('./admin/operations/vulnerability-detail.component').then(m => m.VulnerabilityDetailComponent)
          },
          {
            path: '',
            redirectTo: 'dashboard',
            pathMatch: 'full'
          },
          // Unknown admin paths stay inside the admin portal.
          {
            path: '**',
            redirectTo: 'dashboard'
          }
        ]
      }
    ]
  },
  {
    path: 'client',
    providers: [provideTranslocoScope('client')],
    children: [
      { path: 'login',           canActivate: [clientGuestGuard], loadComponent: () => import('./client/auth/client-auth.component').then(m => m.ClientAuthComponent), data: { mode: 'login' } },
      // Password recovery stays reachable with an active session — the link
      // arrives by email and the user may not know which session is open.
      { path: 'forgot-password', loadComponent: () => import('./client/auth/client-auth.component').then(m => m.ClientAuthComponent), data: { mode: 'forgot' } },
      { path: 'reset-password',  loadComponent: () => import('./client/auth/client-auth.component').then(m => m.ClientAuthComponent), data: { mode: 'reset' } },
      // Where an invitation email lands. Same component as reset: both ask for
      // one new password, only the wording and the mutation differ.
      { path: 'accept-invitation', loadComponent: () => import('./client/auth/client-auth.component').then(m => m.ClientAuthComponent), data: { mode: 'accept' } },
      // Two ways in: forced right after signing in with a password someone
      // else chose, and voluntarily from the header. Guarded because the
      // mutation behind it needs a session — without this an expired one would
      // fail on submit instead of sending the user to sign in.
      { path: 'change-password', canActivate: [clientAuthGuard], loadComponent: () => import('./client/auth/client-auth.component').then(m => m.ClientAuthComponent), data: { mode: 'change' } },
      {
        path: '',
        loadComponent: () => import('./client/shell/client-shell.component').then(m => m.ClientShellComponent),
        canActivate: [clientAuthGuard],
        children: [
          { path: 'projects',              loadComponent: () => import('./client/projects/my-projects.component').then(m => m.MyProjectsComponent) },
          { path: 'projects/:id',         loadComponent: () => import('./client/projects/my-project-detail.component').then(m => m.MyProjectDetailComponent) },
          { path: 'vulnerabilities',      loadComponent: () => import('./client/vulnerabilities/my-vulnerabilities.component').then(m => m.MyVulnerabilitiesComponent) },
          // Presentation-only guard: every resolver behind this screen also
          // checks the role against the database.
          { path: 'users',                canActivate: [clientAdminGuard], loadComponent: () => import('./client/users/my-company-users.component').then(m => m.MyCompanyUsersComponent) },
          { path: 'vulnerabilities/:id',  loadComponent: () => import('./client/vulnerabilities/my-vulnerability-detail.component').then(m => m.MyVulnerabilityDetailComponent) },
          { path: '',                     redirectTo: 'projects', pathMatch: 'full' },
          // Unknown client paths stay inside the client portal.
          { path: '**',                   redirectTo: 'projects' },
        ]
      },
    ]
  },
  {
    path: '',
    pathMatch: 'full',
    redirectTo: () => resolveRootRedirect()
  },
  {
    path: '**',
    redirectTo: () => resolveRootRedirect()
  }
];
